import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FaGlobe,
  FaBlog,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaQuora,
  FaReddit,
} from 'react-icons/fa';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */
interface SocialLink {
  name: string;
  url: string;
  icon: React.ElementType;
  color: string;
  description: string;
}

/* -------------------------------------------------------------------------- */
/*  Sub-menus (Instagram & Facebook)                                          */
/* -------------------------------------------------------------------------- */
const instagramAccounts: SocialLink[] = [
  {
    name: 'EximPe Official',
    url: 'https://www.instagram.com/exim.pe/',
    icon: FaInstagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    description: 'Export & import made easy.',
  },
  {
    name: 'Borderless Business Network',
    url: 'https://www.instagram.com/borderless_business_network/',
    icon: FaInstagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    description: 'Tips for working with international clients.',
  },
  {
    name: 'EximPe Trade',
    url: 'https://www.instagram.com/eximpe_trade/',
    icon: FaInstagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    description: 'Trade strategies for importers & exporters.',
  },
];

const facebookAccounts: SocialLink[] = [
  {
    name: 'EximPe Official',
    url: 'https://www.facebook.com/eximpe',
    icon: FaFacebook,
    color: 'bg-blue-600',
    description: 'News, product releases & events.',
  },
  {
    name: 'EximPe Global Trade',
    url: 'https://www.facebook.com/profile.php?id=61575652275536',
    icon: FaFacebook,
    color: 'bg-blue-600',
    description: 'Expert strategies, seamless trade solutions and tips for importers and exporters to thrive globally',
  },
  {
    name: 'Borderless Business Network ',
    url: 'https://www.facebook.com/profile.php?id=61575984930626',
    icon: FaFacebook,
    color: 'bg-blue-600',
    description: 'Expert strategies, seamless payment solutions & insider tips for thriving with international clients',
  },
];

/* -------------------------------------------------------------------------- */
/*  Main card list                                                            */
/* -------------------------------------------------------------------------- */
const socialLinks: SocialLink[] = [
  {
    name: 'Website',
    url: 'https://eximpe.com/?utm_source=linktree&utm_medium=website&utm_campaign=social_links',
    icon: FaGlobe,
    color: 'bg-[#26a887]',
    description: 'Explore our world — official updates, services & more.',
  },
  {
    name: 'Blog',
    url: 'https://eximpe.com/blog/?utm_source=linktree&utm_medium=blog&utm_campaign=social_links',
    icon: FaBlog,
    color: 'bg-[#26a887]',
    description: 'Discover insights, stories and expert articles.',
  },
  {
    name: 'Instagram',
    url: '#',
    icon: FaInstagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    description: 'Click to choose the right Insta channel.',
  },
  {
    name: 'Facebook',
    url: '#',
    icon: FaFacebook,
    color: 'bg-blue-600',
    description: 'Click to choose the right Facebook page.',
  },
  {
    name: 'Twitter',
    url: 'https://x.com/eximpe/',
    icon: FaTwitter,
    color: 'bg-black',
    description: 'Catch real-time news, thoughts & announcements.',
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/eximpe/',
    icon: FaLinkedin,
    color: 'bg-blue-700',
    description: 'Network with us for professional news & opportunities.',
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@Eximpe1',
    icon: FaYoutube,
    color: 'bg-red-600',
    description: 'Watch our stories unfold — tutorials, insights & more.',
  },
  {
    name: 'WhatsApp',
    url: 'https://api.whatsapp.com/send?phone=916909016944&text=Hello%20EximPe',
    icon: FaWhatsapp,
    color: 'bg-green-500',
    description: 'Chat with us directly — quick support & updates.',
  },
  {
    name: 'Quora',
    url: 'https://www.quora.com/profile/EximPe-1',
    icon: FaQuora,
    color: 'bg-[#ba1c1b]',
    description: 'Explore answers, share knowledge, join the discussion.',
  },
  {
    name: 'Reddit',
    url: 'https://www.reddit.com/user/EximPe_Official/',
    icon: FaReddit,
    color: 'bg-[#ea580b]',
    description: 'Join the conversation, insights & community threads.',
  },
];

/* -------------------------------------------------------------------------- */
/*  Card component                                                            */
/* -------------------------------------------------------------------------- */
function SocialLinkCard({
  link,
  index,
  isLoaded,
  widthClass = 'w-full',
  subLinks,
}: {
  link: SocialLink;
  index: number;
  isLoaded: boolean;
  widthClass?: string;
  subLinks?: SocialLink[];
}) {
  const Icon = link.icon;
  const [open, setOpen] = useState(false);

  const Wrapper: React.ElementType = subLinks ? 'div' : 'a';
  const wrapperProps = subLinks
    ? { onClick: () => setOpen((v) => !v), role: 'button', tabIndex: 0 }
    : { href: link.url, target: '_blank', rel: 'noopener noreferrer' };

  return (
    <Wrapper
      {...wrapperProps}
      className={`group relative transform transition-all duration-500 hover:scale-105 ${widthClass} ${
        isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
      } h-full`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Base card */}
      <div className="flex flex-col h-full bg-white/80 backdrop-blur-md rounded-xl overflow-hidden border-2 border-white shadow-lg transition-shadow duration-300 group-hover:shadow-2xl">
        <div className={`${link.color} p-6 flex justify-center items-center text-white relative h-28`}>
          <Icon className="w-8 h-8 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" />
        </div>
        <div className="flex flex-col justify-between flex-grow p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-1">{link.name}</h3>
          <p className="text-gray-600">{link.description}</p>
        </div>
      </div>

      {/* Pop-up submenu */}
      {subLinks && (
        <div
          className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-4 flex flex-col gap-4 w-72 z-50 transition-all duration-300 ${
            open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
          }`}
        >
          {subLinks.map((s, i) => {
            const SubIcon = s.icon;
            return (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-lg shadow-lg p-3 hover:scale-[1.03] transition"
                style={{ transitionDelay: `${i * 75}ms` }}
              >
                <span className={`${s.color} flex-none p-2 rounded-full text-white flex items-center justify-center`}>
                  <SubIcon className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{s.name}</p>
                  <p className="text-xs text-gray-600">{s.description}</p>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </Wrapper>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main App                                                                  */
/* -------------------------------------------------------------------------- */
export default function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => setIsLoaded(true), []);

  return (
    <>
      <Helmet>
        <title>Follow EximPe on Social Media</title>
        <meta
          name="description"
          content="Stay connected with EximPe across LinkedIn, Twitter, Facebook, Instagram and more for the latest updates and insights on global trade."
        />
      </Helmet>

      <div className="relative min-h-screen bg-[#54ab9] text-white pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background circles */}
        <div className="absolute inset-0 w-full z-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
            <circle cx="80%" cy="70%" r="300" fill="#e3f3ee" />
            <circle cx="10%" cy="35%" r="100" fill="#e3f3ee" />
          </svg>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Header */}
          <header
            className={`text-center mb-16 transform ${
              isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            } transition-all duration-1000`}
          >
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Connect With EximPe</h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Stay connected with us through our various platforms and be part of our global network.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <div className="px-4 py-2 bg-[#27a887] rounded-full text-white shadow-md backdrop-blur-sm">
                Instant E-FIRA Generation
              </div>
              <div className="px-4 py-2 bg-[#27a887] rounded-full text-white shadow-md backdrop-blur-sm">
                Zero FX-Markup
              </div>
              <div className="px-4 py-2 bg-[#27a887] rounded-full text-white shadow-md backdrop-blur-sm">
                100+ Countries
              </div>
            </div>
          </header>

          {/* Website & Blog */}
          <div
            className={`flex justify-center flex-wrap gap-6 mb-8 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } transition-opacity duration-1000 delay-300`}
          >
            {socialLinks
              .filter((l) => l.name === 'Website' || l.name === 'Blog')
              .map((link, i) => (
                <SocialLinkCard
                  key={link.name}
                  link={link}
                  index={i}
                  isLoaded={isLoaded}
                  widthClass="md:w-[300px] w-[390px]"
                />
              ))}
          </div>

          {/* Main grid */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } transition-opacity duration-1000 delay-300`}
          >
            {socialLinks
              .filter((l) => l.name !== 'Website' && l.name !== 'Blog')
              .map((link, i) => (
                <SocialLinkCard
                  key={link.name}
                  link={link}
                  index={i + 10}
                  isLoaded={isLoaded}
                  subLinks={
                    link.name === 'Instagram'
                      ? instagramAccounts
                      : link.name === 'Facebook'
                      ? facebookAccounts
                      : undefined
                  }
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
}
